/**
 * POC animated lobby — procedural particle systems for the single effects canvas.
 *
 * Coins are radial-gradient discs with a rim, pre-rendered once per size to small offscreen
 * canvases and `drawImage`d (squashed horizontally to fake the spin); confetti are rotating rects
 * filled from a fixed palette. No per-frame gradient creation, no images, no DOM. Everything is
 * seeded so a frozen frame (Storybook / Chromatic) is deterministic.
 */

export type ParticleKind = 'coins' | 'confetti';

export interface ParticleSystemSpec {
  kind: ParticleKind;
  /** Viewport size in CSS px. */
  width: number;
  height: number;
  count?: number;
  seed?: number;
}

export interface ParticleSystem {
  readonly kind: ParticleKind;
  readonly alive: boolean;
  /** Advances the simulation by `dtMs` (clamped, so a background tab cannot explode the physics). */
  step: (dtMs: number) => void;
  /** Draws every live particle. The context must already be scaled to CSS px. */
  draw: (ctx: CanvasRenderingContext2D) => void;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  /** Rotation / spin phase in radians. */
  angle: number;
  spin: number;
  /** Palette or sprite index. */
  variant: number;
  /** ms since birth; negative while waiting to spawn. */
  age: number;
  life: number;
  alive: boolean;
}

export const DEFAULT_COIN_COUNT = 110;
export const DEFAULT_CONFETTI_COUNT = 140;
const COIN_LIFE_MS = 2500;
const CONFETTI_LIFE_MS = 2500;
const GRAVITY = 1500; // px/s²
const COIN_DRAG = 0.4; // 1/s
const CONFETTI_DRAG = 1.6; // 1/s
const FADE_MS = 400;
const MAX_STEP_MS = 50;

const COIN_SIZES = [14, 19, 25];
const CONFETTI_COLORS = ['#ff4d6d', '#ffd166', '#06d6a0', '#4cc9f0', '#f72585', '#ffffff'];

// Same generator as SnowRain: identical seed, identical shower.
const seeded = (seed: number): (() => number) => {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
};

/* ── Coin sprites: rendered once per size, shared by every shower ─────────────────────────── */

const SPRITE_SCALE = 2;
let coinSprites: HTMLCanvasElement[] | null = null;

const renderCoinSprite = (diameter: number): HTMLCanvasElement => {
  const px = Math.ceil(diameter * SPRITE_SCALE);
  const sprite = document.createElement('canvas');
  sprite.width = px;
  sprite.height = px;
  const ctx = sprite.getContext('2d');
  if (!ctx) return sprite;

  const r = px / 2;
  const body = ctx.createRadialGradient(r * 0.7, r * 0.65, r * 0.1, r, r, r);
  body.addColorStop(0, '#fff3b0');
  body.addColorStop(0.45, '#ffd34d');
  body.addColorStop(0.85, '#d99a00');
  body.addColorStop(1, '#8a5a00');
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(r, r, r - 1, 0, Math.PI * 2);
  ctx.fill();

  // Rim.
  ctx.lineWidth = Math.max(1.5, px * 0.07);
  ctx.strokeStyle = 'rgba(120, 70, 0, 0.7)';
  ctx.beginPath();
  ctx.arc(r, r, r - ctx.lineWidth, 0, Math.PI * 2);
  ctx.stroke();

  // Inner ring.
  ctx.lineWidth = Math.max(1, px * 0.05);
  ctx.strokeStyle = 'rgba(255, 245, 200, 0.55)';
  ctx.beginPath();
  ctx.arc(r, r, r * 0.62, 0, Math.PI * 2);
  ctx.stroke();

  return sprite;
};

const getCoinSprites = (): HTMLCanvasElement[] => {
  if (!coinSprites) coinSprites = COIN_SIZES.map(renderCoinSprite);
  return coinSprites;
};

/* ── Systems ──────────────────────────────────────────────────────────────────────────────── */

const createCoins = (spec: ParticleSystemSpec, random: () => number): Particle[] => {
  const count = spec.count ?? DEFAULT_COIN_COUNT;
  return Array.from({ length: count }, () => {
    const variant = Math.floor(random() * COIN_SIZES.length);
    return {
      x: random() * spec.width,
      y: -30 - random() * 80,
      vx: (random() - 0.5) * 160,
      vy: 120 + random() * 260,
      size: COIN_SIZES[variant] ?? 19,
      angle: random() * Math.PI * 2,
      spin: (2 + random() * 6) * (random() < 0.5 ? -1 : 1),
      variant,
      // Spread the spawn over the first 900 ms so it reads as a shower, not a wall.
      age: -random() * 900,
      life: COIN_LIFE_MS,
      alive: true,
    };
  });
};

const createConfetti = (spec: ParticleSystemSpec, random: () => number): Particle[] => {
  const count = spec.count ?? DEFAULT_CONFETTI_COUNT;
  return Array.from({ length: count }, (_, i) => {
    // Two cannons in the bottom corners firing up and inwards.
    const fromLeft = i % 2 === 0;
    const originX = fromLeft ? spec.width * 0.08 : spec.width * 0.92;
    const direction = fromLeft ? 1 : -1;
    const speed = 900 + random() * 700;
    const elevation = (55 + random() * 30) * (Math.PI / 180);
    return {
      x: originX,
      y: spec.height + 10,
      vx: Math.cos(elevation) * speed * direction,
      vy: -Math.sin(elevation) * speed,
      size: 6 + random() * 6,
      angle: random() * Math.PI * 2,
      spin: (4 + random() * 10) * (random() < 0.5 ? -1 : 1),
      variant: Math.floor(random() * CONFETTI_COLORS.length),
      age: -random() * 250,
      life: CONFETTI_LIFE_MS,
      alive: true,
    };
  });
};

const stepParticle = (p: Particle, dt: number, drag: number, height: number): void => {
  p.age += dt * 1000;
  if (p.age < 0) return;
  if (p.age > p.life || p.y > height + 60) {
    p.alive = false;
    return;
  }
  p.vy += GRAVITY * dt;
  const damping = 1 - drag * dt;
  p.vx *= damping;
  p.vy *= damping;
  p.x += p.vx * dt;
  p.y += p.vy * dt;
  p.angle += p.spin * dt;
};

const alphaFor = (p: Particle): number => {
  const remaining = p.life - p.age;
  return remaining < FADE_MS ? Math.max(0, remaining / FADE_MS) : 1;
};

const drawCoins = (ctx: CanvasRenderingContext2D, particles: Particle[]): void => {
  const sprites = getCoinSprites();
  for (const p of particles) {
    if (!p.alive || p.age < 0) continue;
    const sprite = sprites[p.variant];
    if (!sprite) continue;
    // Squash on the x axis to fake a coin spinning around its vertical axis.
    const squash = Math.max(0.12, Math.abs(Math.cos(p.angle)));
    const w = p.size * squash;
    ctx.globalAlpha = alphaFor(p);
    ctx.drawImage(sprite, p.x - w / 2, p.y - p.size / 2, w, p.size);
  }
  ctx.globalAlpha = 1;
};

const drawConfetti = (ctx: CanvasRenderingContext2D, particles: Particle[]): void => {
  for (const p of particles) {
    if (!p.alive || p.age < 0) continue;
    ctx.globalAlpha = alphaFor(p);
    ctx.fillStyle = CONFETTI_COLORS[p.variant] ?? '#ffffff';
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    // A flat rect whose height breathes with the spin reads as a tumbling strip.
    ctx.fillRect(-p.size / 2, (-p.size * 0.6 * Math.cos(p.angle * 1.7)) / 2, p.size, Math.max(1.5, p.size * 0.6 * Math.abs(Math.cos(p.angle * 1.7))));
    ctx.restore();
  }
  ctx.globalAlpha = 1;
};

export const createParticleSystem = (spec: ParticleSystemSpec): ParticleSystem => {
  const random = seeded(spec.seed ?? (Date.now() & 0xffff));
  const particles = spec.kind === 'coins' ? createCoins(spec, random) : createConfetti(spec, random);
  const drag = spec.kind === 'coins' ? COIN_DRAG : CONFETTI_DRAG;
  const draw = spec.kind === 'coins' ? drawCoins : drawConfetti;
  let alive = particles.length > 0;

  return {
    kind: spec.kind,
    get alive() {
      return alive;
    },
    step: (dtMs: number) => {
      const dt = Math.min(dtMs, MAX_STEP_MS) / 1000;
      let any = false;
      for (const p of particles) {
        if (!p.alive) continue;
        stepParticle(p, dt, drag, spec.height);
        if (p.alive) any = true;
      }
      alive = any;
    },
    draw: (ctx) => draw(ctx, particles),
  };
};

/** Runs the simulation forward in fixed steps — a deterministic frame for Storybook. */
export const advanceParticleSystem = (system: ParticleSystem, totalMs: number, stepMs = 16): void => {
  for (let elapsed = 0; elapsed < totalMs; elapsed += stepMs) {
    system.step(stepMs);
  }
};
